import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Users, TrendingUp, Clock } from "lucide-react";

const CALENDLY_LINK = "https://calendly.com/pragma-cx/discovery-call";

const stats = [
  { icon: Users, value: "150+", label: "Agents Deployed" },
  { icon: TrendingUp, value: "30+", label: "Happy Clients" },
  { icon: Clock, value: "99.5%", label: "Uptime SLA" },
];

export default function CTASection() {
  return (
    <section className="py-24 relative overflow-hidden border-t border-border">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 via-background to-pink-500/5" />
      <div className="absolute inset-0 grid-bg opacity-30" />
      
      {/* Animated Orbs */}
      <motion.div
        className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"
        animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 8, repeat: Infinity }}
      />
      <motion.div
        className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl"
        animate={{ scale: [1.2, 1, 1.2], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 8, repeat: Infinity, delay: 4 }}
      />

      <div className="container relative z-10">
        <motion.div
          className="text-center max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          {/* Badge */}
          <motion.span
            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-400/10 to-pink-500/10 border border-cyan-400/30 text-cyan-400 text-sm font-mono rounded-full mb-8"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
          >
            <Sparkles className="w-4 h-4" />
            Transform Your Support Today
          </motion.span>

          {/* Main Headline */}
          <h2 className="text-foreground mb-6">
            Ready to Scale Your{" "}
            <span className="text-cyan-400 text-glow-cyan">
              Customer Support?
            </span>
          </h2>

          {/* Subheading */}
          <p className="text-muted-foreground text-lg mb-10 max-w-2xl mx-auto">
            Join growing companies that have reduced support costs by 60% while
            improving customer satisfaction. Deploy professional agents in just
            14 business days.
          </p>

          {/* CTA Buttons */}
          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <motion.a
              href={CALENDLY_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="neon-button group"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              data-testid="cta-section-primary"
            >
              Schedule Free Consultation
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </motion.a>
            <motion.button
              onClick={() => {
                const element = document.querySelector("#pricing");
                if (element) {
                  element.scrollIntoView({ behavior: "smooth" });
                }
              }}
              className="ghost-button"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              data-testid="cta-section-secondary"
            >
              View Pricing
            </motion.button>
          </motion.div>

          {/* Trust Stats */}
          <motion.div
            className="pt-10 border-t border-border flex flex-col sm:flex-row items-center justify-center gap-8 sm:gap-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
          >
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={stat.label}
                  className="text-center flex items-center gap-3"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Icon className="w-5 h-5 text-cyan-400" />
                  <div>
                    <div className="text-2xl font-mono font-bold text-cyan-400">
                      {stat.value}
                    </div>
                    <p className="text-gray-500 text-sm">{stat.label}</p>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
